import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  getCapacityQueryKey,
  listReservationsQueryKey,
  type PoolCapacityView,
  reservationSlotsError,
  reservationStatus,
  reserveCapacityMutation,
} from '@/entities/capacity'
import { useTelephonyPools } from '@/entities/telephony'
import { errorMessage, formatDateTime } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { SelectField, TextField } from '@/shared/ui/field'
import { SlideOver } from '@/shared/ui/slide-over'

// Ввод локального времени → ISO для бэка; пустая строка означает «не задано».
const toIso = (local: string): string | null => {
  if (!local) return null
  const at = new Date(local)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

const localInput = (at: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`
}

const ID_TOKEN = /^[A-Za-z0-9_-]{1,64}$/

export function ReservationForm({
  open,
  pools,
  cellSlots,
  onClose,
}: {
  open: boolean
  pools: PoolCapacityView[]
  cellSlots: number
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { pools: telephonyPools } = useTelephonyPools()
  const [form, setForm] = useState({
    id: '',
    pool: '',
    accountId: '',
    slots: String(cellSlots),
    fromAt: '',
    plannedUntil: localInput(new Date(Date.now() + 4 * 3600 * 1000)),
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const create = useMutation({
    ...reserveCapacityMutation(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listReservationsQueryKey() })
      void qc.invalidateQueries({ queryKey: getCapacityQueryKey() })
      onClose()
    },
  })

  const sellable = pools.find((p) => p.pool === form.pool)?.sellable_slots
  const idError =
    form.id.trim() !== '' && !ID_TOKEN.test(form.id.trim())
      ? 'Латиница, цифры, «_» и «-», до 64 символов: идентификатором названы сабжект очереди и консьюмер'
      : undefined
  const slotsError = reservationSlotsError(form.slots, cellSlots, sellable) ?? undefined
  const fromIso = toIso(form.fromAt)
  const untilIso = toIso(form.plannedUntil)
  const windowError =
    untilIso && fromIso && untilIso <= fromIso ? 'Окно вывернуто: конец раньше начала' : undefined
  const ready =
    form.id.trim() !== '' &&
    !idError &&
    form.pool !== '' &&
    !slotsError &&
    !!untilIso &&
    !windowError

  // Пул для заявки берём из картины ёмкости, но подписываем именем каталога:
  // в картине пул назван только слагом.
  const poolName = (id: string) => telephonyPools.find((p) => p.id === id)?.name ?? ''

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Заявка на ёмкость"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            pending={create.isPending}
            disabled={!ready}
            onClick={() =>
              create.mutate({
                body: {
                  id: form.id.trim(),
                  pool: form.pool,
                  slots: Number(form.slots),
                  planned_until: untilIso ?? '',
                  ...(form.accountId.trim() ? { account_id: form.accountId.trim() } : {}),
                  ...(fromIso ? { from_at: fromIso } : {}),
                },
              })
            }
          >
            Завести
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="rounded-control bg-raise px-4 py-2.5 text-caption text-muted">
          Приём заявки не тратит ни рубля: пока окно прогрева не подошло, под ней нет ни одного
          сервера. Конец заявке ставит только отзыв — срок окна лишь подсказка планирования.
        </p>

        <TextField
          label="Идентификатор"
          required
          placeholder="campaign-2026-03"
          value={form.id}
          onChange={(e) => set({ id: e.target.value })}
          error={idError}
          hint="Занимается навсегда: им названы сабжект очереди и durable-консьюмер"
        />

        <SelectField
          label="Пул"
          required
          value={form.pool}
          onChange={(e) => set({ pool: e.target.value })}
          hint={
            sellable !== undefined
              ? `Пул продаёт ${sellable} слотов; обещано заявкам — на доске выше`
              : 'Линии этого пула нужны заявке'
          }
        >
          <option value="" disabled>
            {pools.length > 0 ? 'Выбери пул' : 'Пулов нет'}
          </option>
          {pools.map((p) => (
            <option key={p.pool} value={p.pool}>
              {poolName(p.pool) ? `${poolName(p.pool)} · ${p.pool}` : p.pool} · продаёт{' '}
              {p.sellable_slots}
            </option>
          ))}
        </SelectField>

        <TextField
          label="Слотов"
          required
          type="number"
          min={cellSlots}
          step={cellSlots}
          value={form.slots}
          onChange={(e) => set({ slots: e.target.value })}
          error={slotsError}
          hint={`Одновременных звонков. Кратно ${cellSlots}: ячейка 1C + 2L несёт ${cellSlots} звонка и между заявками не делится`}
        />

        <TextField
          label="Аккаунт потребителя"
          placeholder="client-204"
          value={form.accountId}
          onChange={(e) => set({ accountId: e.target.value })}
          hint="Пусто — операторская заявка: ёмкость под тест-звонок, демо и прогрев перед приёмкой"
        />

        <TextField
          label="Начало окна"
          type="datetime-local"
          value={form.fromAt}
          onChange={(e) => set({ fromAt: e.target.value })}
          hint="Пусто — ёмкость нужна сейчас"
        />

        <TextField
          label="Окно до"
          required
          type="datetime-local"
          value={form.plannedUntil}
          onChange={(e) => set({ plannedUntil: e.target.value })}
          error={windowError}
          hint="Подсказка планирования: по нему ничего не выключается, просрочка лишь помечает заявку"
        />

        {create.isError && <WindowConflict error={create.error} />}
      </div>
    </SlideOver>
  )
}

interface ConflictRow {
  id: string
  slots: number
  from_at: string
  planned_until: string
  status: string
}

// 409 по окну несёт мешающие заявки — по ним оператор сдвигает своё окно или
// дожидается их отзыва. Просроченный сосед виден по окну, дотянутому до сейчас.
function WindowConflict({ error }: { error: unknown }) {
  const conflicts =
    typeof error === 'object' &&
    error !== null &&
    Array.isArray((error as { conflicts?: unknown }).conflicts)
      ? ((error as { conflicts: ConflictRow[] }).conflicts ?? [])
      : []

  return (
    <div className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
      <p>{errorMessage(error)}</p>
      {conflicts.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 text-caption">
          {conflicts.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{c.id}</span>
              <Chip>{c.slots} слотов</Chip>
              <span className="text-muted">
                {formatDateTime(c.from_at)} — {formatDateTime(c.planned_until)} ·{' '}
                {reservationStatus(c.status).label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
